import styled from '@emotion/styled'
import { Button } from '@mui/material'
import Skeleton from '@mui/material/Skeleton'
import React, { useCallback, useState, FC, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import * as Analytics from '../../../common/utils/analytics'
import { isFromAppAtom } from '../../../state/common/atoms'
import AVSearch from '../../services/search/AVSearch'
import { breakpoint } from '../../utils/breakpoint'
import { useDebouncedValue } from '../../utils/debounce'
import { scale } from '../../utils/scale'
import { ColumnFlex } from '../Layout/Layout'
import ModalHeader from '../Modal/ModalHeader/ModalHeader'
import { Text } from '../Typography/Typography'

import SearchAutocomplete from './SearchAutocomplete'
import NoResults from './SearchResults/NoResults'
import ResultsFound from './SearchResults/ResultsFound'
import ShortSearchWord from './SearchResults/ShortSearchWord'
import { SearchTreeItem } from './types'

const Container = styled(ColumnFlex)<{ isFromApp?: boolean }>`
  @media (max-width: ${breakpoint.sm}px) {
    padding: ${(props) => (props.isFromApp ? 0 : scale(3))} 0 0 0;
  }
  min-height: 90vh;
`
const Heading = styled(Text)`
  margin-top: ${scale(2)};
  margin-bottom: ${scale(2)};
  padding: 0 16px;
`
Heading.defaultProps = { $size: 400, $weight: 'Medium', $height: 'Medium' }

const BreadcrumbDescription = styled(Text)`
  margin-bottom: ${scale(2)};
  padding: 0 16px;
`

BreadcrumbDescription.defaultProps = { $size: 300, $height: 'Large' }

const BreadcrumbError: React.FC<
  React.PropsWithChildren<{
    error?: string
    buttonText: string
    onButtonClick: () => void
  }>
> = ({ error, buttonText, onButtonClick }) => {
  if (!error) {
    return null
  }

  return (
    <ColumnFlex>
      <BreadcrumbDescription>{error}</BreadcrumbDescription>
      <div>
        <Button variant="outlined" onClick={onButtonClick}>
          {buttonText}
        </Button>
      </div>
    </ColumnFlex>
  )
}

const BreadcrumbHighlight = styled.div`
  padding: 0px 16px;
`

const ShowPopularButton = styled(Button)`
  height: 40px;
  min-width: 176px;
  align-self: center;
`

const PendingContainer = styled(ColumnFlex)`
  gap: 16px;
  margin-bottom: 12px;
`

type ListProps = {
  title: string
  startIcon?: JSX.Element
  endIcon?: boolean
  description?: string
}

// eslint-disable-next-line @typescript-eslint/ban-types
interface Props<T extends object, V extends object> {
  popularData: Array<SearchTreeItem<T>>
  allData?: Array<SearchTreeItem<T>>
  searchIndex: AVSearch<V>
  breadcrumb: Array<SearchTreeItem<T>>
  getOptionLabel(option: V): string
  optionListModifier?(options: V[]): V[]
  popularHeading?: string
  popularLabel?: string
  allDataHeading?: string
  allDataLabel?: string
  searchPending?: boolean
  placeholder?: string
  searchFieldLabel: string
  backLabel?: string
  onBreadcrumbChange(
    updaterOrBreadcrumb:
      | ((currVal: Array<SearchTreeItem<T> | V>) => Array<SearchTreeItem<T> | V>)
      | Array<SearchTreeItem<T>>
  ): void
  onSelectAndClose(item: T | V): void
  onClose(): void
  noteProvider?(item: SearchTreeItem<T>): JSX.Element
  useTracking?: boolean
  groupSearchResultsBy?(option: V): string
  open: boolean
  breadcrumbHighlightProvider?(breadcrumb: SearchTreeItem<T> | undefined): JSX.Element | null
  mapDataToListProps: (data: SearchTreeItem<T>) => ListProps
  mapSearchResultsToListProps: (data: V) => ListProps
}

// eslint-disable-next-line @typescript-eslint/ban-types
const Search = <T extends object, V extends object>({
  popularData,
  allData,
  searchIndex,
  breadcrumb,
  popularHeading,
  popularLabel,
  allDataHeading,
  allDataLabel,
  backLabel,
  onBreadcrumbChange,
  onSelectAndClose,
  onClose: onCloseOriginal,
  noteProvider,
  searchPending,
  placeholder,
  searchFieldLabel,
  useTracking,
  open,
  breadcrumbHighlightProvider,
  mapDataToListProps,
  mapSearchResultsToListProps,
  groupSearchResultsBy,
}: Props<T, V>): JSX.Element => {
  const { t } = useTranslation()
  const isFromApp = useRecoilValue(isFromAppAtom)
  const [showAllData, setShowAlldata] = useState<boolean>(false)
  const [selectedNoteItem, setSelectedNoteItem] = useState<SearchTreeItem<T> | null>(null)
  const [internalSearchPending, setInternalSearchPending] = useState<boolean>(false)
  const [breadcrumbPending, setBreadcrumbPending] = useState<boolean>(false)
  const [noResults, setNoResults] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>('')
  const [autoFocusOnResults, moveAutoFocusToResults] = useState<boolean | null>(null)

  const onBack =
    breadcrumb.length > 0
      ? () => {
          popBreadcrumb()
          moveAutoFocusToResults(true)
        }
      : undefined

  const onClose = useCallback(() => {
    onCloseOriginal()
  }, [onCloseOriginal])

  const onSearchTreeItemSelect = useCallback(
    (item: SearchTreeItem<T>) => {
      setSearchWord('')
      moveAutoFocusToResults(true)
      if ('note' in item) {
        if (item.note) {
          setSelectedNoteItem(item)
          onBreadcrumbChange((previous) => [...previous, item])
          return
        }
      }

      if ('item' in item && item.item && (!item.children || !item.children.length)) {
        onSelectAndClose(item.item)
        return
      }

      if ('children' in item && (!item.children || item.children?.length)) {
        onClose
      }

      onBreadcrumbChange((previous) => [...previous, item])
      return
    },
    [onBreadcrumbChange, onClose, onSelectAndClose]
  )

  const getBreadcrumbName = useCallback(() => {
    return breadcrumb.length > 0 ? breadcrumb.slice(-1)[0].name : undefined
  }, [breadcrumb])

  const getBreadcrumbChildren = useCallback(() => {
    return breadcrumb.length > 0 ? breadcrumb.slice(-1)[0].children : undefined
  }, [breadcrumb])

  const getBreadcrumbDescription = useCallback(() => {
    return breadcrumb.length > 0 ? breadcrumb.slice(-1)[0].description : undefined
  }, [breadcrumb])

  const getBreadcrumbError = useCallback(() => {
    return breadcrumb.length > 0 ? breadcrumb.slice(-1)[0].error : undefined
  }, [breadcrumb])

  const getBreadcrumbTop = useCallback(() => {
    return breadcrumb.length > 0 ? breadcrumb.slice(-1)[0] : undefined
  }, [breadcrumb])

  const popBreadcrumb = useCallback(() => {
    onBreadcrumbChange((previous) => previous.slice(0, -1))
  }, [onBreadcrumbChange])

  const mapBreadCrumbsToUiData = useCallback(() => {
    const breadcrumbChildren =
      getBreadcrumbChildren()?.map((i) => {
        return { ...i, parentName: '' }
      }) ?? []

    return breadcrumbChildren.map((l) => {
      return {
        ...mapDataToListProps(l),
        onClick: () => onSearchTreeItemSelect(l),
        group: l.group,
      }
    })
  }, [getBreadcrumbChildren, mapDataToListProps, onSearchTreeItemSelect])

  const initialUiItems = useMemo(
    () =>
      popularData.map((l) => {
        return {
          ...mapDataToListProps(l),
          onClick: () => onSearchTreeItemSelect(l),
        }
      }),
    [popularData, mapDataToListProps, onSearchTreeItemSelect]
  )

  const onSearchResultItemSelect = useCallback(
    (item: V) => {
      if (breadcrumb.length === 0) {
        setBreadcrumbPending(true)
      }
      const possibleUiData = breadcrumb.length > 0 ? mapBreadCrumbsToUiData() : initialUiItems
      setUiItems(possibleUiData)
      setSearchWord('')
      onSelectAndClose(item)
    },
    [onSelectAndClose, breadcrumb.length, mapBreadCrumbsToUiData, initialUiItems]
  )

  const allUiItems = allData?.map((l) => {
    return {
      ...mapDataToListProps(l),
      onClick: () => onSearchTreeItemSelect(l),
    }
  })

  const [uiItems, setUiItems] = useState(initialUiItems)

  const togglePopularSwitch = () => {
    if (showAllData === false) {
      setUiItems(allUiItems ?? [])
    } else {
      setUiItems(initialUiItems)
    }
    setShowAlldata(!showAllData)
    moveAutoFocusToResults(true)
  }

  const debouncedInputValue = useDebouncedValue(searchWord, 300)

  const popNote = useCallback(() => {
    setSelectedNoteItem(null)
    popBreadcrumb()
  }, [popBreadcrumb])

  const breadcrumbHighlight =
    breadcrumbHighlightProvider &&
    breadcrumb.length > 0 &&
    breadcrumbHighlightProvider?.(getBreadcrumbTop())

  useEffect(() => {
    if (breadcrumb.length > 0 && searchWord.length === 0) {
      const breadCrumbUiItems = mapBreadCrumbsToUiData()
      setBreadcrumbPending(false)
      setInternalSearchPending(false)
      setUiItems(breadCrumbUiItems)
      return
    }

    if (breadcrumb.length === 0 && searchWord.length < 3) {
      setNoResults(false)
      setInternalSearchPending(false)
      setUiItems(initialUiItems)
    }

    if (searchWord.length === 3) {
      setInternalSearchPending(true)
    }
  }, [breadcrumb.length, searchWord.length, initialUiItems, mapBreadCrumbsToUiData])

  useEffect(() => {
    if (debouncedInputValue.length > 2) {
      setInternalSearchPending(true)
      searchIndex.search(debouncedInputValue, 10).then((results) => {
        setNoResults(false)
        setInternalSearchPending(false)
        const resultsToUiItems = results.map((o) => {
          return {
            ...mapSearchResultsToListProps(o),
            onClick: () => onSearchResultItemSelect(o),
            group: groupSearchResultsBy ? groupSearchResultsBy(o) : undefined,
          }
        })
        setUiItems(resultsToUiItems)
        if (results.length === 0) {
          setNoResults(true)
        }

        if (useTracking) {
          Analytics.trackAutocomplete(debouncedInputValue, results.length)
        }
      })
    }
  }, [
    debouncedInputValue,
    groupSearchResultsBy,
    mapSearchResultsToListProps,
    onSearchResultItemSelect,
    searchIndex,
    useTracking,
  ])

  if (selectedNoteItem && noteProvider) {
    return (
      <Container isFromApp={isFromApp}>
        <ModalHeader
          open={open}
          onClose={onClose}
          onBack={popNote}
          enableMobileClose={open}
          backLabel={backLabel}
        />
        {noteProvider(selectedNoteItem)}
      </Container>
    )
  }

  const PendingContent: FC = () => (
    <PendingContainer>
      <Heading as="h2">{t('component.search.resultsHeading')}</Heading>
      {[...new Array(4)].map((_, index) => (
        <Skeleton key={`skeleton-${index}`} variant="rectangular" width="100%" height="48px" />
      ))}
    </PendingContainer>
  )

  const Content: FC = () => {
    const shortSearchWord = searchWord.length > 0 && searchWord.length < 3
    const dataHeading = showAllData
      ? allDataHeading ?? t('component.search.allDataHeading')
      : popularHeading ?? t('component.search.popularHeading')
    const breadCrumbHeading = getBreadcrumbName()

    const heading = breadCrumbHeading || dataHeading
    const headingId = `search-result-heading-${heading.replaceAll(' ', '-')}-id`

    const onResultsNotFound = () => {
      setSearchWord('')
      setNoResults(false)
      setUiItems(initialUiItems)
      setShowAlldata(true)
    }

    if (internalSearchPending || breadcrumbPending) {
      return <PendingContent />
    }

    if (shortSearchWord) {
      return <ShortSearchWord />
    }

    if (noResults) {
      return <NoResults onClick={onResultsNotFound} />
    }

    return (
      <ColumnFlex>
        {(searchWord.length === 0 || (breadcrumb.length > 0 && searchWord.length === 0)) && (
          <>
            <Heading as="h2" id={headingId}>
              {heading}
            </Heading>

            {getBreadcrumbDescription() && (
              <BreadcrumbDescription>{getBreadcrumbDescription()}</BreadcrumbDescription>
            )}
            <BreadcrumbError
              error={getBreadcrumbError()}
              onButtonClick={popBreadcrumb}
              buttonText={t('common.back')}
            />
          </>
        )}
        {breadcrumbHighlight && <BreadcrumbHighlight>{breadcrumbHighlight}</BreadcrumbHighlight>}
        <ResultsFound
          items={uiItems}
          searchWord={searchWord}
          autoFocus={autoFocusOnResults}
          headingId={headingId}
        />
        {searchWord.length === 0 && breadcrumb.length === 0 && (
          <ShowPopularButton
            variant="outlined"
            onClick={togglePopularSwitch}
            data-cy="search-showPopularButton"
          >
            {showAllData
              ? popularLabel ?? t('component.search.popularLink')
              : allDataLabel ?? t('component.search.allServices')}
          </ShowPopularButton>
        )}
      </ColumnFlex>
    )
  }

  return (
    <Container isFromApp={isFromApp}>
      <ModalHeader
        open={open}
        onClose={onClose}
        onBack={onBack}
        enableMobileClose={open}
        backLabel={backLabel}
      />
      <SearchAutocomplete
        label={searchFieldLabel}
        placeholder={placeholder}
        searchWord={searchWord}
        setSearchWord={setSearchWord}
        autoFocus={!autoFocusOnResults}
        moveAutoFocusToResults={moveAutoFocusToResults}
      />
      {searchPending ? <PendingContent /> : <Content />}
    </Container>
  )
}

export default Search
